import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  MenuItem,
  Select,
  debounce,
  Autocomplete,
  CardHeader,
  alpha,
  Typography,
  IconButton,
  SvgIcon,
} from "@mui/material";
import { useDispatch, useSelector } from "src/store";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import LoadingButton from "@mui/lab/LoadingButton";
import toast from "react-hot-toast";
import { GeneratedTopic } from "src/types/topic";
import { thunks as promptsThunks } from "src/thunks/prompts";
import axios, { CancelTokenSource } from "axios";
import { PromptMessage, PromptMessageUser } from "src/types/prompts";
import { parse } from "partial-json";
import { thunks as contentTemplatesThunks } from "src/thunks/content-templates";
import { ContentTemplate } from "src/types/content-template";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { MagicWand as MagicWandIcon } from "@phosphor-icons/react/dist/ssr/MagicWand";
import { CaretRight } from "@phosphor-icons/react/dist/ssr/CaretRight";
import { CaretDown } from "@phosphor-icons/react/dist/ssr/CaretDown";
// Update the validation schema to handle ContentTemplate
const createValidationSchema = (t: any) =>
  z.object({
    instruction: z.string().min(1, {
      message: t(tokens.general.validators.required) as string,
    }),
    contentTemplate: z
      .union([
        z.string(),
        z.object({
          id: z.string(),
          name: z.string(),
          // add other ContentTemplate fields as needed
        }),
      ])
      .refine((val) => val && (typeof val === "string" || val.id), {
        message: t(tokens.general.validators.required) as string,
      }),
  });

// Define the Values type using Zod inference
type FormValues = z.infer<ReturnType<typeof createValidationSchema>>;

interface TopicContentGenerationFormProps {
  onTextGenerate: (text: string) => void;
  onTitleGenerate: (title: string) => void;
  onFinish: () => void;
  isCollapsed?: boolean;
  hasTitle?: boolean;
}

export const TopicContentGenerationForm: FC<
  TopicContentGenerationFormProps
> = ({ onTextGenerate, onFinish, onTitleGenerate, hasTitle, isCollapsed }) => {
  const { t, i18n } = useTranslation();
  const { strategyId } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCollapsedMode, setIsCollapsedMode] = useState(isCollapsed ?? false);

  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource>(
    axios.CancelToken.source()
  );

  const { isLoading: isContentTemplatessLoading, items: contentTemplates } =
    useSelector((state) => state.contentTemplates);
  const [contentTemplateInputValue, setContentTemplateInputValue] =
    useState("");

  const fetchTemplates = useMemo(
    () =>
      debounce(async (request: string) => {
        await dispatch(
          contentTemplatesThunks.getContentTemplates(strategyId!, {
            top: 25,
            page: 1,
            text: request,
          })
        );
      }, 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [strategyId]
  );

  useEffect(() => {
    let active = true;

    if (active) {
      fetchTemplates(contentTemplateInputValue);
    }

    return () => {
      active = false;
    };
  }, [fetchTemplates, contentTemplateInputValue]);

  // Create the validation schema
  const validationSchema = createValidationSchema(t);

  // Initialize the form
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      instruction: "",
      contentTemplate: "basic",
    },
    resolver: zodResolver(validationSchema),
  });

  const createContentPromptMessages = (data: FormValues): PromptMessage[] => {
    const messages: PromptMessage[] = [
      {
        user: PromptMessageUser.System,
        content: `Generate content for the topic`,
      },
    ];

    const selectedTemplate = contentTemplates.items.find(
      (t) => t.id === data.contentTemplate
    );

    if (selectedTemplate) {
      messages.push({
        user: PromptMessageUser.User,
        content: `${selectedTemplate?.instruction}`,
      });

      if (selectedTemplate?.examples.length > 0) {
        var examples = "\n\nUse the following examples:\n";
        selectedTemplate?.examples.forEach((example, index) => {
          examples += `Example ${index + 1} start:\n${example}\n`;
          examples += `Example ${index + 1} end\n`;
        });

        messages.push({
          user: PromptMessageUser.User,
          content: examples,
        });
      }
    }

    if (data.instruction) {
      messages.push({
        user: PromptMessageUser.User,
        content: data.instruction,
      });
    }

    return messages;
  };

  const createTitlePromptMessages = (data: FormValues): PromptMessage[] => {
    const messages: PromptMessage[] = [
      {
        user: PromptMessageUser.System,
        content: `Generate a short title for the topic without any formatting.`,
      },
      {
        user: PromptMessageUser.User,
        content: data.instruction,
      },
    ];

    return messages;
  };

  const onSubmit = async (data: FormValues): Promise<void> => {
    setIsSubmitting(true);

    const contentMessages = createContentPromptMessages(data);
    const titleMessages = createTitlePromptMessages(data);

    if (!hasTitle) {
      try {
        await dispatch(
          promptsThunks.runChatPrompt(
            strategyId!,
            i18n.language,
            {
              messages: titleMessages,
            },
            (text) => {
              onTitleGenerate(text);
            },
            () => {
              toast.error(t(tokens.general.formError));
            },
            (text) => {
              onFinish();
            },
            cancelTokenSource
          )
        );
      } catch (error) {
        console.error(error);
      }
    }

    try {
      await dispatch(
        promptsThunks.runChatPrompt(
          strategyId!,
          i18n.language,
          {
            messages: contentMessages,
          },
          (text) => {
            onTextGenerate(text);
          },
          () => {
            toast.error(t(tokens.general.formError));
          },
          (text) => {
            onFinish();
          },
          cancelTokenSource
        )
      );
    } catch (error) {
      console.error(error);
    }
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card
        sx={{
          border: "1px solid var(--mui-palette-primary-main)",
          //backgroundColor: "var(--mui-palette-secondary-50)",
        }}
      >
        <CardHeader
          title={
            <Stack
              sx={{
                width: "100%",
              }}
              justifyContent={"space-between"}
              alignItems={"center"}
              spacing={2}
              direction={"row"}
            >
              <Typography variant="subtitle1">AI generation</Typography>
              <IconButton
                sx={{
                  color: "white",
                }}
                onClick={(e) => {
                  setIsCollapsedMode(!isCollapsedMode);
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <SvgIcon>
                  {isCollapsedMode && <CaretRight />}
                  {!isCollapsedMode && <CaretDown />}
                </SvgIcon>
              </IconButton>
            </Stack>
          }
          sx={{
            color: "white",
            py: 1,
            px: 2,
            cursor: "pointer",
            backgroundColor: "var(--mui-palette-primary-main)",
            "&:hover": {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.95),
              cursor: "pointer",
              //opacity: 0.5,
            },
          }}
          onClick={() => {
            setIsCollapsedMode(!isCollapsedMode);
          }}
        ></CardHeader>
        {!isCollapsedMode && (
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="contentTemplate"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    getOptionLabel={(option: ContentTemplate | string) =>
                      typeof option === "string" ? option : option.name
                    }
                    options={contentTemplates.items}
                    fullWidth
                    loading={isContentTemplatessLoading}
                    isOptionEqualToValue={(option, value) =>
                      option.id ===
                      (typeof value === "string" ? value : value.id)
                    }
                    onChange={(
                      event: any,
                      newValue: ContentTemplate | null
                    ) => {
                      field.onChange(newValue ? newValue.id : "");
                    }}
                    onInputChange={(event, newInputValue) => {
                      setContentTemplateInputValue(newInputValue);
                    }}
                    renderInput={(params): JSX.Element => (
                      <TextField
                        {...params}
                        error={!!errors.contentTemplate}
                        helperText={errors.contentTemplate?.message}
                        size="small"
                        fullWidth
                        name="contentTemplate"
                        label={t(
                          tokens.topics.details.content.generator.form
                            .contentTemplate
                        )}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="instruction"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.instruction}
                    fullWidth
                    multiline
                    minRows={3}
                    helperText={errors.instruction?.message}
                    label={t(
                      tokens.topics.details.content.generator.form.instruction
                    )}
                    placeholder={t(
                      tokens.topics.details.content.generator.form
                        .instructionPlaceholder
                    )}
                  />
                )}
              />

              <Stack
                alignItems="center"
                direction="row"
                justifyContent="flex-start"
                spacing={1}
              >
                <LoadingButton
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  variant="contained"
                  startIcon={<MagicWandIcon />}
                >
                  {t(tokens.topics.details.content.generator.buttons.generate)}
                </LoadingButton>

                {isSubmitting && (
                  <LoadingButton
                    type="button"
                    onClick={() => {
                      cancelTokenSource.cancel();
                      setCancelTokenSource(axios.CancelToken.source());
                    }}
                    variant="outlined"
                  >
                    {t(tokens.general.buttons.cancel)}
                  </LoadingButton>
                )}
              </Stack>
            </Stack>
          </CardContent>
        )}
      </Card>
    </form>
  );
};
